import { Controller } from '@hotwired/stimulus';

// #rrggbb, case-insensitive on input (what a person can type) -- but see
// syncFromColor below, which always mirrors the browser's own lowercase form.
const HEX_PATTERN = /^#[0-9a-f]{6}$/i;

// Mirrors the color swatch and the text field onto each other and performs NO
// color arithmetic -- no hex->rgb, no lighten/darken, no contrast ratio, no
// parseInt on a color value. Color derivation has exactly one implementation,
// server-side (app/models/votes/customizations/theme/color_math.rb), so the
// preview and the saved result can never diverge (plan.md D1; D6 SOFT VETO --
// client behaviour may never become a server-side guard).
export default class extends Controller {
  static targets = ['text', 'color'];

  declare textTarget: HTMLInputElement;
  declare colorTarget: HTMLInputElement;

  // <input type="color"> always normalises its own value to lowercase #rrggbb;
  // mirror that string as-is into the text field so the two never disagree on case.
  syncFromColor(): void {
    this.textTarget.value = this.colorTarget.value.toLowerCase();
    this.dispatchChange(this.textTarget);
  }

  // Setting `.value` on <input type="color"> to a string that doesn't match
  // #rrggbb is silently ignored by the browser, which would otherwise let a
  // half-typed hex clobber the swatch with whatever the browser falls back to.
  // Validate first so the swatch only ever reflects a complete value.
  syncFromText(): void {
    const value = this.textTarget.value;
    if (!HEX_PATTERN.test(value)) return;

    this.colorTarget.value = value;
    this.dispatchChange(this.colorTarget);
  }

  // Setting `.value` from JS fires no native event. Without this explicit
  // dispatch, listeners elsewhere (the preview frame reload, a contrast
  // warning) never learn the value changed.
  private dispatchChange(target: HTMLInputElement): void {
    target.dispatchEvent(new Event('change', { bubbles: true }));
  }
}
